import type { CSSProperties, ReactNode } from 'react';
import {
  type RouletteBetId,
  type RouletteBetNum,
  type RoulettePocket,
  type RouletteWheel,
} from '@tv-casino/shared';
import { chipStyle, useSeatHue } from '../lib/playerColors.js';
import type {
  RouletteLayout,
  RouletteSpot,
  RouletteSpotKey,
  RouletteStackMap,
} from '@tv-casino/viewmodels/components/rouletteFelt.js';
import {
  columnSpots,
  dozenSpots,
  numberSpots,
  outsideSpots,
  zerosFor,
} from '@tv-casino/viewmodels/components/rouletteFelt.js';
import styles from './RouletteTable.module.css';

/* Which cells the felt prints, and in what order, is shared data: the phone,
   the TV and this stylesheet all draw one layout (`rouletteFelt`). */
export type { RouletteLayout, RouletteSpot };
export { numberSpots, zerosFor };

export interface RouletteTableProps {
  /** The zeros the table spins decide the top-left corner of the layout. */
  wheel: RouletteWheel;
  /** The landed pocket, for the dolly that marks the winning number. */
  pocket?: RoulettePocket | null;
  /** Every chip on the layout, spot by spot. */
  stacks?: RouletteStackMap;
  /** Spots the spin paid, for the winner's flash. */
  marks?: Partial<Record<RouletteSpotKey, 'win'>>;
  /** The spot the next chip would land on. */
  armed?: RouletteSpotKey | null;
  /** Phone only: does this spot take a chip right now? */
  canDrop?: (betId: RouletteBetId, num?: RouletteBetNum) => boolean;
  onDrop?: (betId: RouletteBetId, num?: RouletteBetNum) => void;
  /** Phone only: take this seat's chips back off the spot. */
  onPull?: (betId: RouletteBetId, num?: RouletteBetNum) => void;
  canPull?: (betId: RouletteBetId, num?: RouletteBetNum) => boolean;
  /** The TV renders the same layout twice the size. */
  size?: 'phone' | 'tv';
  /** Which way round the felt is printed. Phones default to the tall turn. */
  layout?: RouletteLayout;
  /** The wheel and its readout ride above the layout on the TV. */
  head?: ReactNode;
}

const cx = (...parts: (string | false | null | undefined)[]) => parts.filter(Boolean).join(' ');

const toneClass = (tone: RouletteSpot['tone']): string =>
  tone === 'red'
    ? (styles.red ?? '')
    : tone === 'black'
      ? (styles.black ?? '')
      : tone === 'green'
        ? (styles.green ?? '')
        : '';

/**
 * A roulette layout, printed the way it is on the felt: the zeros down the
 * left, three rails of twelve, the 2-to-1 rail down the right, dozens and the
 * even-money row beneath. Every chip sits where it was bet, whoever bet it.
 * The phone passes handlers and gets a betting board; the TV passes none and
 * gets the same picture, read-only.
 *
 * A phone held upright gets that felt turned a quarter turn — zeros across
 * the top, twelve rows of three, dozens down the right — because twelve
 * columns across 300 points of screen is a cell no thumb can hit.
 */
export function RouletteTable({
  wheel,
  pocket = null,
  stacks = {},
  marks = {},
  armed = null,
  canDrop,
  onDrop,
  onPull,
  canPull,
  size = 'phone',
  layout = size === 'tv' ? 'wide' : 'tall',
  head,
}: RouletteTableProps) {
  // A chip on the felt wears its player's colour, never its denomination —
  // the same hue the seat's plate carries. The phone and the TV are looking
  // at one table, so a stack has to read as the same chip on both.
  const hue = useSeatHue();
  const playerStyle = (seatIndex: number): CSSProperties => chipStyle(hue(seatIndex));
  const live = Boolean(onDrop);
  const tall = layout === 'tall';
  const zeros = zerosFor(wheel);
  const numbers = numberSpots(layout);

  const spot = (s: RouletteSpot) => {
    const here = stacks[s.key] ?? [];
    const mine = here.find((c) => c.mine) ?? null;
    const others = here.filter((c) => !c.mine);
    const open = live && (canDrop?.(s.betId, s.num) ?? true);
    const won = Boolean(marks[s.key]);
    const isDolly = pocket !== null && s.betId === 'straight' && s.num === pocket;
    const body = (
      <>
        <span className={styles.label}>{s.label}</span>
        <span className={styles.chips}>
          {others.slice(0, 2).map((c) => (
            <span
              key={c.seatIndex}
              className={cx(styles.chip, styles.chipPlayer)}
              style={playerStyle(c.seatIndex)}
              title={`${c.name ?? `Seat ${c.seatIndex + 1}`} · ${c.amount}`}
            >
              {c.amount}
            </span>
          ))}
          {others.length > 2 && <span className={styles.more}>+{others.length - 2}</span>}
        </span>
      </>
    );

    return (
      <div
        key={s.key}
        data-roulette-spot={s.key}
        className={cx(
          styles.spot,
          toneClass(s.tone),
          armed === s.key && styles.spotArmed,
          mine && styles.spotMine,
          won && styles.spotWon,
          isDolly && styles.spotDolly,
          live && !open && styles.spotShut,
        )}
      >
        {live ? (
          <button
            type="button"
            className={styles.drop}
            disabled={!open}
            onClick={() => onDrop?.(s.betId, s.num)}
            aria-label={s.label}
          >
            {body}
          </button>
        ) : (
          <div className={styles.drop}>{body}</div>
        )}
        {mine &&
          (onPull && (canPull?.(s.betId, s.num) ?? true) ? (
            <button
              type="button"
              className={cx(styles.myChip, styles.chipPlayer)}
              style={playerStyle(mine.seatIndex)}
              onClick={() => onPull(s.betId, s.num)}
              aria-label={`Take back ${mine.amount} on ${s.label}`}
            >
              {mine.amount}
            </button>
          ) : (
            <span
              className={cx(styles.myChip, styles.myChipLocked, styles.chipPlayer)}
              style={playerStyle(mine.seatIndex)}
              aria-label={`${mine.amount} on ${s.label}`}
            >
              {mine.amount}
            </span>
          ))}
      </div>
    );
  };

  const zerosGroup = (
    <div className={styles.zeros} role="group" aria-label="Zeros">
      {zeros.map((z) => spot(z))}
    </div>
  );

  const numbersGroup = (
    <div className={styles.numbers} role="group" aria-label="Numbers 1 to 36">
      {numbers.map((n) => spot(n))}
    </div>
  );

  const dozensGroup = (
    <div className={styles.dozens} role="group" aria-label="Dozens">
      {dozenSpots().map((s) => spot(s))}
    </div>
  );

  const outsidesGroup = (
    <div className={styles.outsides} role="group" aria-label="Even-money bets">
      {outsideSpots().map((s) => spot(s))}
    </div>
  );

  /* Turned tall, the rail pays the column standing above it, so it reads the
     other way round: 1st column on the left — `columnSpots` owns that turn. */
  const columnsGroup = (
    <div className={styles.columnRail} role="group" aria-label="Columns">
      {columnSpots(layout).map((s) => spot(s))}
    </div>
  );

  if (tall) {
    return (
      <div className={cx(styles.felt, styles.feltTall)}>
        {head && <div className={styles.head}>{head}</div>}
        {zerosGroup}
        {numbersGroup}
        {dozensGroup}
        {columnsGroup}
        {outsidesGroup}
      </div>
    );
  }

  return (
    <div className={cx(styles.felt, size === 'tv' && styles.feltTv)}>
      {head && <div className={styles.head}>{head}</div>}

      <div className={styles.gridRow}>
        {zerosGroup}

        <div className={styles.main}>
          {numbersGroup}
          {columnsGroup}
        </div>
      </div>

      {dozensGroup}
      {outsidesGroup}
    </div>
  );
}
